import React, { useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { FormikTouched } from 'formik';
import { FieldKeyModel, SmartFormDefinitionModel } from '@utmost.co/enterprise-models';
import { Loading } from '@utmost.co/utmost-design-system';
import { ElementModifier, ElementWithMeta, SmartformElement, SmartFormElements } from '../types';
import { withDefaultFieldsAndMeta } from '../elements/withDefaultFieldsAndMeta';
import { SmartformContextProps, SmartformProvider } from './controllers';
import { GET_SMART_FORM_DEFINITION_TEMPLATE } from './queries/GET_SMART_FORM_DEFINITION_TEMPLATE';
import { type SmartformProps, type ValueObject, Smartform } from './Smartform';
import type { SmartFormFeatureFlags } from './SmartFormFeatures.types';
import { URLParams } from './utils/URLParams';
import { InputModel } from './utils';
import { customFieldsPrefixer } from './utils/model/customFieldsPrefixer';
import { addReadOnlyToMeta } from './utils/formControllerFieldMeta';
import { FormDescription } from './layouts';
import { DiffMode } from './utils/diffMode/types';
import { UserEventTracker } from '../integrations/userEvents/UserEventsTypes';

export interface SmartformWithTemplateProps<Type extends string, Model = InputModel> {
  onSubmit: (values: DeepPartial<Model>, setSubmitting: (isSubmitting: boolean) => void) => void | Promise<any>;
  useUrlQuery?: () => URLSearchParams;
  contextMap?: Map<string, object>;
  initialValues?: DeepPartial<Model>;
  initialTouched?: FormikTouched<DeepPartial<Model>>;
  formElements: SmartFormElements<Type, Model>;
  defaultFormElements: SmartformElement[];
  formId?: string;
  formVersion?: number;
  marginStyle?: Partial<React.CSSProperties>;
  hideEmptyHints?: boolean;
  actionButtons?: React.ReactNode;
  validate?: SmartformProps<any>['validate'];
  enableReinitialize?: boolean;
  formControllerProps: SmartformContextProps;
  onChange?: (values: DeepPartial<Model>) => void;
  onError?: (errors: ValueObject) => void;
  hidden?: boolean;
  diffMode?: DiffMode;
  patchError?: { code: string; message: string };
  childrenPlacement?: 'top' | 'bottom';
  submitted?: boolean;
  elementModifiers?: ElementModifier<ElementWithMeta<Model>>[]; // modify elements,
  onTrackUserEvent?: UserEventTracker;

  // Smart Form featureFlags should be used very judiciously for short lived temporary
  // escape hatch type feature flags.
  featureFlags?: SmartFormFeatureFlags;

  /** Optional custom component to render when user lacks permission to view the smart form */
  renderPermissionDenied?: () => React.ReactNode;

  /** Callback fired when permission to view the smart form is denied */
  onPermissionDenied?: () => void;

  /** Callback fired when the smart form definition is inactive (unpublished or deleted) */
  onInactive?: () => void;

  /*! watchFieldKeysForBlur - Field keys to watch for blur events. Requires onFieldBlur to also be provided. */
  watchFieldKeysForBlur?: FieldKeyModel[];
  /*! onFieldBlur - Fired when a field whose meta.fieldKey is in watchFieldKeysForBlur transitions to touched.
   * The model path is also passed, since multiple elements (e.g. legal name vs. preferred name) can share the
   * same fieldKey and need to be told apart by consumers. */
  onFieldBlur?: (fieldKey: FieldKeyModel, value: any, path: string) => void;
}

/* SmartformWithTemplate is the super parent/wrapper of all Smart Form elements in BPE. */
function SmartformWithTemplate<Type extends string, Model = InputModel>({
  children,
  onSubmit,
  useUrlQuery = () => new URLSearchParams(),
  contextMap,
  formId,
  formVersion,
  marginStyle,
  initialValues,
  initialTouched,
  formElements,
  actionButtons,
  defaultFormElements,
  validate,
  enableReinitialize,
  childrenPlacement,
  hideEmptyHints,
  formControllerProps,
  onChange,
  onError,
  hidden,
  patchError,
  diffMode,
  submitted,
  elementModifiers,
  featureFlags,
  onTrackUserEvent,
  renderPermissionDenied,
  onPermissionDenied,
  onInactive,
  watchFieldKeysForBlur,
  onFieldBlur
}: SmartformWithTemplateProps<Type, Model> & { children?: any }) {
  const urlQuery = useUrlQuery();

  // Refactor target: Extract the template getting and handling to the caller (OC)
  // Reason: SmartForms should not know anything about how to talk to
  const templateId = formId || urlQuery.get(URLParams.FORM_UID);
  const [getData, { loading, data, error }] = useLazyQuery<{
    smartFormDefinition: SmartFormDefinitionModel;
  }>(GET_SMART_FORM_DEFINITION_TEMPLATE, {
    variables: { uid: templateId, version: formVersion ?? null }
  });

  React.useEffect(() => {
    if (templateId) getData();
  }, [getData, templateId]);

  const smartFormTemplate: { elements: SmartformElement[]; config?: { description?: string } } | null = useMemo(
    () => (data && data.smartFormDefinition ? JSON.parse((data.smartFormDefinition as any).template) : null),
    [data]
  );

  const elements = useMemo(() => {
    const useDefault = error || !templateId;
    const result = ((useDefault ? defaultFormElements : smartFormTemplate?.elements) ?? []).map((el) =>
      withDefaultFieldsAndMeta(el, formElements)
    );
    if (!elementModifiers) {
      return result;
    }
    return elementModifiers.reduce<ElementWithMeta<Model>[]>((acc, modifier) => {
      return modifier(acc);
    }, result);
  }, [defaultFormElements, error, formElements, smartFormTemplate?.elements, templateId, elementModifiers]);

  const prefixedInitialValues = useMemo(() => customFieldsPrefixer(initialValues), [initialValues]);
  const prefixedInitialTouched = useMemo(() => customFieldsPrefixer(initialTouched), [initialTouched]);

  const permissionDenied = templateId && isSmartFormPermissionDenied(error);

  useEffect(() => {
    if (permissionDenied && onPermissionDenied) {
      onPermissionDenied();
    }
  }, [permissionDenied, onPermissionDenied]);

  useEffect(() => {
    if (!onInactive || !templateId || loading || permissionDenied) return;

    const status = data?.smartFormDefinition?.status;
    const isInactiveStatus = status === 'DELETED' || status === 'UNPUBLISHED';
    const isMissing = !!data && !data.smartFormDefinition;
    const isNotFound = isSmartFormNotFound(error);

    // Add a check if iFramed so to not redirect for GWG task forms.
    const isIFramed = window.self !== window.top;

    if ((isInactiveStatus || isMissing || isNotFound) && !isIFramed) {
      onInactive();
    }
  }, [data, error, loading, onInactive, permissionDenied, templateId]);

  if (templateId) {
    if (loading || (!smartFormTemplate && !error)) {
      return <Loading />;
    }
  }

  if (permissionDenied) {
    if (renderPermissionDenied) {
      return <>{renderPermissionDenied()}</>;
    }
    // Default fallback if no custom renderer provided
    return (
      <div data-test="smart-form-permission-denied" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <p>You do not have permission to view this form.</p>
      </div>
    );
  }

  const fieldMeta = addReadOnlyToMeta(elements, formControllerProps.fieldMeta);

  return (
    <SmartformProvider
      value={{
        ...formControllerProps,
        fieldMeta,
        featureFlags,
        onTrackUserEvent,
        elements: elements as ElementWithMeta[],
        onFieldBlur
      }}
    >
      {!!smartFormTemplate?.config?.description && (
        <FormDescription>{smartFormTemplate?.config?.description}</FormDescription>
      )}
      <Smartform
        initialValues={prefixedInitialValues}
        initialTouched={prefixedInitialTouched}
        onSubmit={onSubmit}
        useUrlQuery={useUrlQuery}
        contextMap={contextMap}
        validate={validate}
        marginStyle={marginStyle}
        hideEmptyHints={hideEmptyHints}
        actionButtons={actionButtons}
        enableReinitialize={enableReinitialize}
        elements={elements}
        formElements={formElements}
        onChange={onChange}
        childrenPlacement={childrenPlacement}
        hidden={hidden}
        onError={onError}
        diffMode={diffMode}
        patchError={patchError}
        submitted={submitted}
        watchFieldKeysForBlur={watchFieldKeysForBlur}
        onFieldBlur={onFieldBlur}
      >
        {children}
      </Smartform>
    </SmartformProvider>
  );
}

export const isSmartFormPermissionDenied = (error: Error | undefined): boolean => {
  if (!error?.message) return false;
  const msg = error.message.toLowerCase();
  return msg.includes('unauthorized') && msg.includes('smart-form-definition') && msg.includes('deny');
};

export const isSmartFormNotFound = (error: Error | undefined): boolean => {
  if (!error?.message) return false;
  const msg = error.message.toLowerCase();
  return msg.includes('smart form not found');
};

export default SmartformWithTemplate;
